import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import {
  getTherapist,
  approveTherapist,
  rejectTherapist,
  toggleTherapistActive,
  resetAdminStates
} from "../../redux/therapistSlice";
import {
  FiCheck,
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiAward,
  FiBookOpen,
  FiBriefcase,
  FiFileText,
  FiPause,
  FiPlay
} from "react-icons/fi";

const AdminTherapistDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { 
    therapistDetail: therapist, 
    loading, 
    error,
    adminLoading
  } = useSelector((state) => state.therapist);

  // Admin işlemleri için handler'lar
  const handleApproveTherapist = async () => {
    try {
      await dispatch(approveTherapist(id)).unwrap();
      toast.success("Therapist approved successfully!");
      dispatch(getTherapist(id)); // Güncellenmiş bilgileri getir
    } catch (error) {
      toast.error(error || "Failed to approve therapist");
    }
  };

  const handleRejectTherapist = async () => {
    try {
      await dispatch(rejectTherapist(id)).unwrap();
      toast.success("Therapist rejected successfully!");
      dispatch(getTherapist(id));
    } catch (error) {
      toast.error(error || "Failed to reject therapist");
    }
  };

  const handleToggleActive = async () => {
    const newActive = !therapist.isActive;
    try {
      await dispatch(toggleTherapistActive({ therapistId: id, isActive: newActive })).unwrap();
      toast.success(newActive ? "Therapist unsuspended successfully!" : "Therapist suspended successfully!");
    } catch (error) {
      toast.error(error || "Failed to update therapist status");
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getTherapist(id));
    }
    
    // Component unmount olduğunda admin state'ini temizle
    return () => {
      dispatch(resetAdminStates());
    };
  }, [dispatch, id]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!therapist) return <div className="p-6">No therapist data found.</div>;

  const {
    name,
    surname,
    email,
    contact,
    address,
    picture,
    education,
    experience,
    branchIds,
    expertisesTags,
    certificates,
    bio,
    resume,
  } = therapist;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <div className="md:shrink-0 md:mr-6">
        <AdminSideBar />
      </div>
      <div className="flex-1 p-4 md:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-md p-6 md:p-8 max-w-5xl mx-auto">
          {/* Profile Info & Admin Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4 md:space-x-6">
              <img
                src={picture}
                alt={`${name} ${surname}`}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border"
              />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  {name} {surname}
                </h1>
                <p className="text-gray-600 flex items-center gap-2">
                  <FiMail className="w-4 h-4" />
                  {email}
                </p>
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <FiPhone className="w-4 h-4" />
                  {contact?.countryCode} {contact?.phoneNumber}
                </p>
                
                {/* Active / Suspended badge */}
                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  therapist.isActive === false
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {therapist.isActive === false ? 'Suspended' : 'Active'}
                </span>
              </div>
            </div>

            {/* Admin Action Buttons - Only show if status is pending */}
            {therapist.approvalStatus === 'pending' && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  onClick={handleApproveTherapist}
                  disabled={adminLoading}
                  className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 rounded-lg font-medium transition-all bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  <FiCheck className="w-5 h-5" />
                  {adminLoading ? 'Processing...' : 'Approve Therapist'}
                </button>
                
                <button
                  onClick={handleRejectTherapist}
                  disabled={adminLoading}
                  className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 rounded-lg font-medium transition-all bg-red-500 hover:bg-red-600 text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  <FiX className="w-5 h-5" />
                  {adminLoading ? 'Processing...' : 'Reject Therapist'}
                </button>
              </div>
            )}

            {/* Status Message when already processed */}
            {therapist.approvalStatus !== 'pending' && (
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className={`inline-flex flex-col items-center gap-2 px-4 py-2 rounded-lg font-medium ${
                  therapist.approvalStatus === 'approved'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {therapist.approvalStatus === 'approved' ? (
                    <>
                      <div className="flex items-center gap-2">
                        <FiCheck className="w-5 h-5" />
                        Already Approved
                      </div>
                      {therapist.adminApprovalDate && (
                        <div className="text-xs text-green-600">
                          Approved on: {new Date(therapist.adminApprovalDate).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FiX className="w-5 h-5" />
                      Already Rejected
                    </div>
                  )}
                </div>

                {/* Suspend / Unsuspend button — only for approved therapists */}
                {therapist.approvalStatus === 'approved' && (
                  <button
                    onClick={handleToggleActive}
                    disabled={adminLoading}
                    className={`flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 rounded-lg font-medium transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto ${
                      therapist.isActive
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    {therapist.isActive ? (
                      <>
                        <FiPause className="w-5 h-5" />
                        {adminLoading ? 'Processing...' : 'Suspend'}
                      </>
                    ) : (
                      <>
                        <FiPlay className="w-5 h-5" />
                        {adminLoading ? 'Processing...' : 'Unsuspend'}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            
            {/* Left Column */}
            <div className="space-y-6">
              
              {/* Address */}
              {address && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiMapPin className="w-5 h-5 text-blue-500" />
                    <h3 className="font-semibold text-gray-800">Location</h3>
                  </div>
                  <p className="text-gray-600">
                    {address.city}, {address.town}, {address.district}
                  </p>
                </div>
              )}

              {/* Education */}
              {education?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiBookOpen className="w-5 h-5 text-green-500" />
                    <h3 className="font-semibold text-gray-800">Education</h3>
                  </div>
                  <ul className="space-y-2">
                    {education.map((edu, index) => (
                      <li key={edu._id || index} className="text-gray-600">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {edu.school || "School information not available"}
                          </span>
                          {edu.finish && (
                            <span className="text-sm text-gray-500">
                              Graduated: {edu.finish}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Branches */}
              {branchIds?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiBriefcase className="w-5 h-5 text-purple-500" />
                    <h3 className="font-semibold text-gray-800">Specialization Areas</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {branchIds.map((branch) => (
                      <span 
                        key={branch._id}
                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                      >
                        {branch.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">

              {/* Experience */}
              {experience?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiBriefcase className="w-5 h-5 text-orange-500" />
                    <h3 className="font-semibold text-gray-800">Experience</h3>
                  </div>
                  <ul className="space-y-3">
                    {experience.map((exp) => (
                      <li key={exp._id} className="text-gray-600">
                        <div className="flex flex-col">
                          <span className="font-medium">{exp.title}</span>
                          <span className="text-sm text-gray-500">{exp.company}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(exp.start).getFullYear()} -{" "}
                            {exp.finish ? new Date(exp.finish).getFullYear() : "Present"}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Expertises */}
              {expertisesTags?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiAward className="w-5 h-5 text-blue-500" />
                    <h3 className="font-semibold text-gray-800">Expertise Tags</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {expertisesTags.map((tag) => (
                      <span 
                        key={tag._id}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certificates */}
              {certificates?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiAward className="w-5 h-5 text-yellow-500" />
                    <h3 className="font-semibold text-gray-800">Certificates</h3>
                  </div>
                  <ul className="space-y-2">
                    {certificates.map((cert) => (
                      <li key={cert._id} className="text-gray-600">
                        <div className="flex flex-col">
                          <span className="font-medium">{cert.name}</span>
                          <span className="text-sm text-gray-500">{cert.description}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Resume */}
              {resume && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FiFileText className="w-5 h-5 text-red-500" />
                    <h3 className="font-semibold text-gray-800">Resume</h3>
                  </div>
                  <a
                    href={`${resume}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                  >
                    <FiFileText className="w-4 h-4" />
                    View PDF Resume
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Bio Section - Full Width */}
          {bio && (
            <div className="mt-8 bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <FiUser className="w-5 h-5 text-green-500" />
                <h3 className="text-xl font-semibold text-gray-800">About</h3>
              </div>
              <div
                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: bio }}
              />
            </div>
          )}

          {/* Payment Info - Full Width */}
          <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FiUser className="w-5 h-5 text-green-500" />
              Payment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Hourly Rate</p>
                <p className="text-xl font-bold text-green-600">£{therapist.hourlyPrice || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Complete</p>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  therapist.isPaymentComplete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {therapist.isPaymentComplete ? "Complete" : "Incomplete"}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Stripe Setup</p>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  therapist.isStripeComplete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {therapist.isStripeComplete ? "Complete" : "Incomplete"}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Stripe Account Status</p>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  therapist.stripeAccount?.status === 'enabled' ? 'bg-green-100 text-green-800' : 
                  therapist.stripeAccount?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {therapist.stripeAccount?.status || 'Not Set'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Bank Verification</p>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  therapist.bankDetails?.isVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {therapist.bankDetails?.isVerified ? "Verified" : "Not Verified"}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Currency</p>
                <p className="font-medium uppercase">{therapist.paymentSettings?.currency || 'GBP'}</p>
              </div>
            </div>

            {/* Stripe Account Details if available */}
            {therapist.stripeAccount && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-700 mb-2">Stripe Account Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Account ID</p>
                    <p className="font-mono text-xs">{therapist.stripeAccount.accountId || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Charges Enabled</p>
                    <span className={`inline-flex px-2 py-1 rounded text-xs ${
                      therapist.stripeAccount.chargesEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {therapist.stripeAccount.chargesEnabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500">Payouts Enabled</p>
                    <span className={`inline-flex px-2 py-1 rounded text-xs ${
                      therapist.stripeAccount.payoutsEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {therapist.stripeAccount.payoutsEnabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTherapistDetail;
